import React, { useMemo } from "react";
import { normalizeNumbers, useIcon } from "@SeemplicityDev/shared";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../../General/useSeverityAsPriority";
import useAddRef, { UseAddRefOptions } from "../useAddRef";

interface IntegrationsPageProps extends UseAddRefOptions {
  data?: {
    categories: {
      name: string;
      total_datasources_count: number;
      sources: {
        name: string;
        severe_count?: {
          severity: number;
          count: number;
        };
        total_count?: number;
      }[];
    }[];
  };
}

interface DisplatedRowProps {
  categoryName: string;
  sourceName: string;
  severityCount: {
    severity: number;
    count: number;
  };
}

const IntegrationsPage: React.FC<IntegrationsPageProps> = ({
  data,
  addRef,
  index,
}) => {
  const ref = useAddRef({ addRef });
  const categoriesNumber = data?.categories?.length || 0;
  const { getDataSourceIcon } = useIcon("LIGHT");
  const { getSeverityField } = useSeverityAsPriority();

  const categoriesList = useMemo(() => {
    return (data?.categories ?? [])
      .reduce((acc: DisplatedRowProps[], category) => {
        category.sources.forEach((source) => {
          if (!!source.severe_count) {
            acc.push({
              categoryName: category.name,
              sourceName: source.name,
              severityCount: source.severe_count,
            });
          }
        });
        return acc;
      }, [] as DisplatedRowProps[])
      .sort((a, b) => b.severityCount.count - a.severityCount.count);
  }, [data?.categories]);

  return (
    <div className={`eoy-page integrations-page`} ref={ref}>
      <h3>Integration</h3>
      <h4>
        <span className="main">Critical Risk Hotspots by Data Source: </span>
        <span className="sub">
          Top Data Sources Driving the Highest Number of Prioritized Findings
        </span>
      </h4>

      <div className={`categories-container has-${categoriesNumber}`}>
        {categoriesList.map((category, index) => {
          return (
            <React.Fragment key={`${category.categoryName}-${index}`}>
              <span className="category-title">{category.categoryName}</span>
              <div className="sources-container">
                <img
                  src={getDataSourceIcon(category.sourceName, "S") as string}
                  alt={category.sourceName}
                  className="source-icon"
                />
                <span className="source-name">{category.sourceName}</span>
              </div>
              <span
                className="severity-count"
                style={{
                  color: getSeverityField(
                    category.severityCount.severity,
                    SeverityFieldsEnum.COLOR
                  ),
                }}
              >
                {normalizeNumbers(category.severityCount.count)}
              </span>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default IntegrationsPage;
